import { memo } from "react";
import TreemapCell from "./TreemapCell";
import { LocationDetailLevel } from "@atlas-int/data/types";
import { servicesPartnersGroupId, ColorBy, UIView } from "@atlas-int/data";
import type { HierarchyRectangularNode } from "d3-hierarchy";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";
import type { OnHoverParams } from "../hooks";

type TreemapLeaf = HierarchyRectangularNode<EnrichedTradeDataItem>;
type TreemapColorMap = Map<string, string> | ((value: number) => string);

interface TreemapCellsProps {
  leaves: TreemapLeaf[] | undefined;
  view: UIView;
  handleTooltip: (params: OnHoverParams) => void;
  colorBy: ColorBy;
  useColorMap: TreemapColorMap;
  highlightedItem?: string;
  locationLevel?: string;
  servicesWidth?: number;
  nonServices?: boolean;
}

const TreemapCells = ({
  leaves,
  view,
  handleTooltip,
  colorBy,
  useColorMap,
  highlightedItem,
  locationLevel,
  servicesWidth,
  nonServices,
}: TreemapCellsProps) => {
  if (!leaves) return null;

  return (
    <>
      {leaves.map((leaf: TreemapLeaf, i: number) => {
        // Use leaf.id if available, otherwise fall back to leaf.data.productId or leaf.data.id, and finally index
        let cellKey = `cell_${leaf.id || leaf.data?.productId || leaf.data?.id || i}`;
        let useId;

        if (view === UIView.Products) {
          if (leaf.data.topLevelParent === "root") {
            useId = leaf.data.sectorId;
          } else {
            useId = leaf.data.topLevelParent;
          }
        } else if (view === UIView.Markets) {
          // For services partners, always use the services partners group ID
          if (
            leaf.id === servicesPartnersGroupId ||
            leaf.data?.id === servicesPartnersGroupId
          ) {
            useId = servicesPartnersGroupId;
          } else {
            // For non-services partners, use location detail level logic
            if (locationLevel === LocationDetailLevel.region) {
              useId = leaf.data.hybrid_level_1?.id ?? leaf.data.id;
            } else if (
              locationLevel === LocationDetailLevel.subregion
            ) {
              useId = leaf.data.hybrid_level_1?.id ?? leaf.data.id;
            } else {
              useId = leaf.data.topLevelParent;
            }
          }
        }

        return (
          <TreemapCell
            key={cellKey}
            leaf={leaf}
            handleTooltip={handleTooltip}
            colorBy={colorBy}
            colorMap={useColorMap}
            useId={useId}
            highlightedItem={highlightedItem}
            servicesWidth={servicesWidth}
            nonServices={nonServices}
          />
        );
      })}
    </>
  );
};

export default memo(TreemapCells);
